import Link from '@atoms/Link/Link';
import paths from '@constants/paths';
import {
  trackCartPreviewEmpty,
  trackCartPreviewEmptyEditorSuggestion,
  trackCartPreviewEmptyEditorSuggestionLinkClick,
} from '@helpers/analyticsHelpers/trackCartPreview';
import { useTranslations } from 'next-intl';
import { StyledEmptyCart, StyledHeading, StyledTextWrapper } from '@molecules/EmptyCart/EmptyCart.styles';
import React, { useEffect } from 'react';
import useCart from '@hooks/useCart';
import Text from '@atoms/Text/Text';
import useCustomer from '@hooks/useCustomer';
import useResponsive from '@hooks/useResponsive';
import EmptyBag from '@icons/EmptyBag.svg';
import Icon from '@atoms/Icon/Icon';
import { useAppDispatch, useAppSelector } from '@hooks/useAppDispatch';
import { selectMiniCartPreviewIsOpen, setIsCartPreviewOpen } from '@slices/miniCartSlice';

interface Props {
  fromCartPage: boolean;
}

const EmptyCart = ({ fromCartPage }: Props) => {
  const { cart, hasFetchedCart } = useCart();
  const { customer } = useCustomer();
  const isCartEmpty = !cart?.products.length;
  const t = useTranslations('cartPreview');
  const { fromDesktop, isMobile } = useResponsive();
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const isOpenEmptyCart = hasFetchedCart && isCartEmpty && isCartPreviewOpen;
  const isAnonymous = customer?.isAnonymous;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isOpenEmptyCart) return;

    if (isAnonymous) {
      trackCartPreviewEmpty();
    } else {
      fromDesktop ? trackCartPreviewEmptyEditorSuggestion() : trackCartPreviewEmpty();
    }
  }, [isOpenEmptyCart, isAnonymous, fromDesktop]);

  const iconSize = fromCartPage ? (isMobile ? 240 : 320) : 160;

  const titleNode = fromCartPage ? (
    <StyledHeading variant="h1">{t('cart.empty.title')}</StyledHeading>
  ) : (
    <Text type="subhead" size="large">
      {t('cart.empty.title')}
    </Text>
  );

  const trackedLink = (
    href: string,
    labelKey:
      | 'editorSuggestions.logIn'
      | 'editorSuggestions.myLists'
      | 'editorSuggestions.myOrders'
      | 'editorSuggestions.myCommonItems'
  ) => {
    const LinkComponent = (chunks: React.ReactNode) => (
      <Link
        href={href}
        shallow
        onClick={() => {
          trackCartPreviewEmptyEditorSuggestionLinkClick(t(labelKey));
          if (!fromDesktop) dispatch(setIsCartPreviewOpen(false));
        }}
      >
        {chunks}
      </Link>
    );

    // Eslint rule require displayName for functional components
    LinkComponent.displayName = `TrackedLink(${labelKey})`;
    return LinkComponent;
  };

  return (
    <StyledEmptyCart $inCartPage={fromCartPage}>
      {titleNode}
      <Icon svg={EmptyBag} size={iconSize} data-testid="empty-cart-icon" />
      <StyledTextWrapper>
        <>
          {isAnonymous ? (
            <Text type="body" size="small">
              {t.rich('cart.empty.anonymousText', {
                loginLink: trackedLink(paths.USER_LOGIN, 'editorSuggestions.logIn'),
              })}
            </Text>
          ) : (
            <Text type="body" size="small">
              {customer?.isB2BCustomer
                ? t.rich('cart.empty.loggedInTextB2B', {
                    listsLink: trackedLink(paths.MY_LISTS, 'editorSuggestions.myLists'),
                    ordersLink: trackedLink(paths.ACCOUNT_ORDERS, 'editorSuggestions.myOrders'),
                  })
                : t.rich('cart.empty.loggedInText', {
                    commonItemsLink: trackedLink(paths.MY_COMMON_ITEMS, 'editorSuggestions.myCommonItems'),
                    listsLink: trackedLink(paths.MY_LISTS, 'editorSuggestions.myLists'),
                    ordersLink: trackedLink(paths.ACCOUNT_ORDERS, 'editorSuggestions.myOrders'),
                  })}
            </Text>
          )}
        </>
      </StyledTextWrapper>
    </StyledEmptyCart>
  );
};
export default EmptyCart;
